import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, Plus, Menu, X, Loader2, LogOut, KeyRound, ChevronDown } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import NewOrderButton from "../NewOrderButton";

const TITLES = {
  "/": "Dashboard", "/customers": "Customer Master", "/contacts": "Contact Master",
  "/products": "Product Master", "/enquiries": "Enquiry Register", "/quotes/new": "Quote Generator",
  "/quotes": "Quote Register", "/pis": "PI Register", "/tax-invoices": "Tax Invoices", "/followups": "Follow-ups",
  "/reports": "Reports", "/notes": "Notes", "/settings": "Settings",
};

const GROUPS = [
  { key: "customers", label: "Customers", to: (r) => `/customers/${r.id}`, primary: (r) => r.company_name, secondary: (r) => r.industry },
  { key: "enquiries", label: "Enquiries", to: () => `/enquiries`, primary: (r) => r.enquiry_no, secondary: (r) => `${r.company_name} · ${r.product}` },
  { key: "quotes", label: "Quotes", to: () => `/quotes`, primary: (r) => r.quote_no, secondary: (r) => r.customer_name },
  { key: "pis", label: "Proforma Invoices", to: () => `/pis`, primary: (r) => r.pi_no, secondary: (r) => r.customer_name },
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/search`, { params: { q } });
        setResults(r.data);
        setOpen(true);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const total = results ? Object.values(results).reduce((s, a) => s + a.length, 0) : 0;
  const go = (to) => { setOpen(false); setQ(""); setResults(null); nav(to); };

  return (
    <div ref={boxRef} className="relative hidden lg:block w-80">
      <div className="flex items-center gap-2 px-3 h-9 rounded-md border border-slate-200 bg-white text-sm focus-within:border-[#ab0000] transition-colors">
        <Search size={16} className="text-slate-400" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results && setOpen(true)}
          placeholder="Search customers, enquiries, quotes…"
          className="flex-1 bg-transparent outline-none text-slate-700 placeholder:text-slate-400"
        />
        {loading && <Loader2 size={14} className="animate-spin text-slate-400" />}
        {!loading && q && <button onClick={() => { setQ(""); setResults(null); }} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>}
      </div>

      {open && results && (
        <div data-testid="global-search-results" className="absolute mt-2 w-[28rem] right-0 bg-white border border-slate-200 rounded-xl shadow-xl max-h-[70vh] overflow-y-auto z-50">
          {total === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">No matches for "{q}"</div>
          ) : (
            GROUPS.map((g) => {
              const items = results[g.key] || [];
              if (items.length === 0) return null;
              return (
                <div key={g.key} className="py-2">
                  <div className="px-4 py-1 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{g.label}</div>
                  {items.map((r) => (
                    <button key={r.id} onClick={() => go(g.to(r))} data-testid={`search-result-${g.key}-${r.id}`}
                      className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{g.primary(r)}</div>
                        {g.secondary(r) && <div className="text-xs text-slate-500 truncate">{g.secondary(r)}</div>}
                      </div>
                      {r.status && <span className="text-[10px] text-slate-400 shrink-0">{r.status}</span>}
                    </button>
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  if (!user) return null;
  const initials = (user.name || user.email || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const levelLabel = { super_admin: "Super Admin", administrator: "Administrator", user: "User" }[user.access_level] || "User";
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="user-menu-btn"
        className="flex items-center gap-2 h-9 pl-1 pr-2 rounded-full hover:bg-slate-100 transition-colors">
        <span className="w-7 h-7 rounded-full bg-[#ab0000] text-white text-xs font-bold flex items-center justify-center">{initials}</span>
        <span className="hidden sm:block text-sm text-slate-700 font-medium max-w-[120px] truncate">{user.name}</span>
        <ChevronDown size={14} className="text-slate-400 hidden sm:block" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden" data-testid="user-menu">
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="font-semibold text-slate-900 text-sm truncate">{user.name}</div>
            <div className="text-xs text-slate-500 truncate">{user.email}</div>
            <div className="mt-1.5 inline-block text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">{levelLabel}</div>
            {user.plants?.length > 0 && <div className="mt-1 text-[11px] text-slate-400">Plants: {user.plants.join(", ")}</div>}
          </div>
          <button onClick={() => { setOpen(false); setPwOpen(true); }} data-testid="change-password-btn"
            className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"><KeyRound size={15} /> Change password</button>
          <button onClick={logout} data-testid="logout-btn"
            className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"><LogOut size={15} /> Sign out</button>
        </div>
      )}
      {pwOpen && <ChangePasswordDialog onClose={() => setPwOpen(false)} />}
    </div>
  );
}

function ChangePasswordDialog({ onClose }) {
  const { changePassword } = useAuth();
  const [cur, setCur] = useState(""); const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setLoading(true);
    try { await changePassword(cur, pw); toast.success("Password changed"); onClose(); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl space-y-3" data-testid="change-password-dialog">
        <h3 className="font-display font-bold text-lg text-slate-900">Change password</h3>
        <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" className="inp" data-testid="cp-current" />
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className="inp" data-testid="cp-new" />
        <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm new password" className="inp" data-testid="cp-confirm" />
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600">Cancel</button>
          <button type="submit" disabled={loading} className="px-4 py-2 rounded-lg bg-[#ab0000] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60">{loading && <Loader2 size={14} className="animate-spin" />} Update</button>
        </div>
      </form>
    </div>
  );
}

export default function TopBar({ onMobileMenu }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { has } = useAuth();
  const NON_CRM_PREFIXES = ["/production", "/sales-orders", "/work-orders", "/dispatch", "/products",
    "/recipe-master", "/raw-materials", "/mould-master", "/machine-master", "/machines", "/data-import",
    "/compound-costing", "/product-costing", "/users", "/audit-log", "/production-planning", "/mes",
    "/compound-batches", "/production-jobs", "/maintenance", "/breakdowns", "/pm-schedule",
    "/procurement", "/grn", "/vendors", "/rm-stock", "/inventory", "/purchase", "/accounts", "/tooling", "/ai/"];
  const isCrm = !NON_CRM_PREFIXES.some((p) => loc.pathname.startsWith(p));
  const title = TITLES[loc.pathname] ||
    (loc.pathname.startsWith("/customers") ? "Customer" :
     loc.pathname.startsWith("/quotes") ? "Quote Register" : "Sahara");

  return (
    <header data-testid="app-topbar" className="sticky top-0 z-30 h-16 border-b border-slate-200 bg-white/85 backdrop-blur">
      <div className="h-full max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={onMobileMenu} aria-label="Open menu" data-testid="mobile-menu-btn" className="md:hidden p-2 -ml-1 rounded-md hover:bg-slate-100 text-slate-700">
            <Menu size={20}/>
          </button>
          <div className="min-w-0">
            <div className="hidden sm:block text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Sahara Business OS</div>
            <h1 className="font-display text-base sm:text-xl font-semibold tracking-tight text-slate-900 truncate">{title}</h1>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {["customers.view", "enquiries.view", "quotes.view", "pi.view"].some((k) => has(k)) && <GlobalSearch />}
          {isCrm && has("enquiries.create") && (
            <NewOrderButton onSelect={(key) => {
              if (key === "repeat") nav("/pis");
              else nav(`/enquiries?order=${key}`);
            }} />
          )}
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
